import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PiChatTextLight, PiPlusLight, PiTrayLight } from 'react-icons/pi';
import Header from '../components/shared/TailwindComponents/Header';
import Table from '../components/shared/TailwindComponents/Table';
import Skeleton from '../components/shared/TailwindComponents/Skeleton';
import EmptyState from '../components/shared/TailwindComponents/EmptyState';
import MarketplaceGate from '../components/Marketplace/MarketplaceGate';
import { enumLabel } from '../components/Marketplace/marketplaceLabels';
import { LeadStatusBadge, countryName, fmtDate } from '../components/Marketplace/marketplaceUi';
import { Lead, listLeads } from '../services/api/marketplaceService';

// No Urgency: an assessment of the matter is for lawyers, never the client.
const COLUMNS = ['Request', 'Type', 'Country', 'Status', 'Created'];

/** Client side: the requests I have filed. Row → request detail. */
const RequestsContent: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [leads, setLeads] = useState<Lead[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    listLeads('mine')
      .then(setLeads)
      .catch(() => {
        setLeads([]);
        setError(true);
      });
  }, []);

  const rows = useMemo(
    () =>
      (leads ?? []).map((l) => ({
        id: l.uuid,
        uuid: l.uuid,
        Request: (
          <span className="block truncate text-fg-strong" title={l.issue_text ?? undefined}>
            {l.issue_text}
          </span>
        ),
        Type: l.triage ? enumLabel(t, l.triage.issue_type) : '—',
        Country: countryName(t, l.triage?.jurisdiction ?? l.jurisdiction_hint),
        Status: <LeadStatusBadge status={l.status} audience="client" />,
        Created: fmtDate(l.created_at),
      })),
    [leads, t]
  );

  const goNew = () => history.push('/marketplace/new');

  return (
    <div className="flex h-full flex-col overflow-hidden" data-testid="marketplace-requests">
      <Header
        breadcrumbs={[{ name: t('Marketplace'), href: '/marketplace', current: true }]}
        actions={[
          { label: t('Lawyer inbox'), onClick: () => history.push('/marketplace/inbox'), icon: <PiTrayLight className="w-4 h-4" />, variant: 'outline' },
          { label: t('New request'), onClick: goNew, icon: <PiPlusLight className="w-4 h-4" />, variant: 'primary' },
        ]}
      />
      <div className="flex-1 overflow-hidden">
        {leads === null ? (
          <Skeleton variant="table" rows={6} columns={COLUMNS.length} />
        ) : leads.length === 0 ? (
          <EmptyState
            icon={<PiChatTextLight size={40} />}
            headline={error ? t('Could not load your requests') : t('No requests yet')}
            subtext={t('Describe a legal issue and PONS suggests lawyers who can help.')}
            button={{ label: t('New request'), onClick: goNew }}
          />
        ) : (
          <div className="h-full overflow-hidden">
            <Table
              columns={COLUMNS}
              data={rows}
              onRowClick={(row: { uuid: string }) => history.push(`/marketplace/requests/${row.uuid}`)}
              total_items={rows.length}
              total_showing_items={rows.length}
              showingLabel={t('requests')}
              storageKeyPrefix="marketplace-requests"
              defaultColumnWidths={{ Request: 340, Type: 140, Jurisdiction: 120, Urgency: 110, Status: 120, Created: 160 }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const MarketplaceRequestsPage: React.FC = () => (
  <MarketplaceGate skeleton="table">{() => <RequestsContent />}</MarketplaceGate>
);

export default MarketplaceRequestsPage;
